import { Check, Heart, Minus, MoreHorizontal, Plus, Star, Trash2, Upload, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, imageURL, year } from "../api";
import { track } from "../analytics";
import { useAuth } from "../auth";
import { LoadingRows, PageState, TitleDialog } from "../components/Media";
import type { LibraryItem, MyDramaListImportReport, Title, WatchStatus } from "../types";

const filters: { label: string; value: WatchStatus | "all" }[] = [
  { label: "All", value: "all" },
  { label: "Watching", value: "watching" },
  { label: "Plan to watch", value: "planned" },
  { label: "Watched", value: "watched" },
  { label: "Dropped", value: "dropped" },
];

export function LibraryPage() {
  const { user, openSignIn } = useAuth();
  const [items, setItems] = useState<LibraryItem[] | null>(null);
  const [filter, setFilter] = useState<WatchStatus | "all">("all");
  const [selected, setSelected] = useState<Title | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState<MyDramaListImportReport | null>(null);

  const load = useCallback(() => {
    if (!user) return;
    setError(null);
    api<{ items: LibraryItem[] }>("/v1/me/library")
      .then((response) => setItems(response.items))
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : "Your list could not be loaded."));
  }, [user]);

  useEffect(load, [load]);
  const visible = useMemo(() => items?.filter((item) => filter === "all" || item.status === filter) ?? [], [filter, items]);

  if (!user) return <PageState title="Your stories, on every screen" message="Sign in to sync what you plan to watch, what you’re watching, and every favorite." action={<button className="primary-button" onClick={openSignIn}>Sign in to My K-List</button>} />;
  if (error) return <PageState title="Your list is out of reach" message={error} action={<button className="primary-button" onClick={load}>Try again</button>} />;

  const patchItem = async (item: LibraryItem, patch: Partial<Pick<LibraryItem, "status" | "isFavorite" | "currentEpisode" | "currentSeason">>) => {
    setItems((current) => current?.map((value) => value.id === item.id ? { ...value, ...patch } : value) ?? null);
    try {
      await api(`/v1/me/library/${item.id}`, { method: "PATCH", body: JSON.stringify(patch) });
    } catch {
      load();
    }
  };

  const importMyDramaList = async (file: File) => {
    setImporting(true); setError(null);
    try {
      const content = await file.text();
      const extension = file.name.split(".").pop()?.toLowerCase();
      const format = extension === "csv" || extension === "json" ? extension : "auto";
      const response = await api<{ report: MyDramaListImportReport }>("/v1/import/mydramalist", {
        method: "POST",
        body: JSON.stringify({ content, format }),
      });
      setImportReport(response.report);
      track("mydramalist_import_completed", {
        imported: response.report.imported,
        updated: response.report.updated,
        skipped: response.report.skipped,
        unmatched: response.report.unmatched.length,
      });
      load();
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : "The MyDramaList export could not be imported.");
    } finally { setImporting(false); }
  };

  const removeItem = async (item: LibraryItem) => {
    setItems((current) => current?.filter((value) => value.id !== item.id) ?? null);
    try {
      await api(`/v1/me/library/${item.id}`, { method: "DELETE" });
    } catch {
      load();
    }
  };

  return (
    <div className="standard-page library-page">
      <div className="page-heading split-heading">
        <div><p className="eyebrow">나의 목록 · My K-List</p><h1>Your library</h1><p>{items?.length ?? 0} stories, always in sync.</p></div>
        <label className={`secondary-button import-button${importing ? " disabled" : ""}`}><Upload size={16} />{importing ? "Matching titles…" : "Import MyDramaList"}<input type="file" name="mydramalist-file" aria-label="Choose a MyDramaList CSV or JSON export" accept=".csv,.json,text/csv,application/json" disabled={importing} onChange={(event) => { const file = event.target.files?.[0]; if (file) void importMyDramaList(file); event.currentTarget.value = ""; }} /></label>
      </div>
      <div className="filter-strip" role="group" aria-label="Filter library">
        {filters.map((option) => (
          <button key={option.value} className={filter === option.value ? "active" : ""} onClick={() => { track("library_filter_changed", { filter: option.value }); setFilter(option.value); }}>
            {option.label}<span>{items?.filter((item) => option.value === "all" || item.status === option.value).length ?? 0}</span>
          </button>
        ))}
      </div>
      {items === null ? <LoadingRows /> : visible.length === 0 ? (
        <div className="inline-empty"><Check size={28} /><h2>{items.length === 0 ? "Your list is open" : "Nothing in this section"}</h2><p>{items.length === 0 ? "Discover a Korean story and save it here." : "Choose another status to see more."}</p></div>
      ) : (
        <div className="library-grid">
          {visible.map((item) => (
            <LibraryCard
              key={item.id}
              item={item}
              onOpen={() => setSelected(item.title)}
              onFavorite={() => void patchItem(item, { isFavorite: !item.isFavorite })}
              onStatus={(status) => void patchItem(item, { status })}
              onProgress={(currentEpisode, status) => void patchItem(item, { currentEpisode, status })}
              onRemove={() => void removeItem(item)}
            />
          ))}
        </div>
      )}
      {selected && <TitleDialog title={selected} onClose={() => setSelected(null)} onLibraryChange={load} />}
      {importReport && <ImportReportDialog report={importReport} onClose={() => setImportReport(null)} />}
    </div>
  );
}

function LibraryCard({ item, onOpen, onFavorite, onStatus, onProgress, onRemove }: {
  item: LibraryItem;
  onOpen(): void;
  onFavorite(): void;
  onStatus(status: WatchStatus): void;
  onProgress(currentEpisode: number, status: WatchStatus): void;
  onRemove(): void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const poster = imageURL(item.title.posterPath);
  return (
    <article className="library-card">
      <button className="library-poster" onClick={onOpen} aria-label={`Open ${item.title.name}`}>
        {poster ? <img src={poster} alt="" width="500" height="750" loading="lazy" /> : <span className="poster-fallback">K</span>}
        {item.isFavorite && <span className="favorite-pin"><Heart size={14} fill="currentColor" /></span>}
      </button>
      <div className="library-copy">
        <div className="library-title-row"><div><h2>{item.title.name}</h2><p>{year(item.title.releaseDate)} · {item.title.type === "tv" ? "Drama" : "Film"}</p></div>
          <button className="icon-button" onClick={() => setMenuOpen((value) => !value)} aria-label="Library item actions"><MoreHorizontal size={18} /></button>
        </div>
        {menuOpen && <div className="card-menu">
          <button onClick={onFavorite}><Heart size={15} /> {item.isFavorite ? "Remove favorite" : "Favorite"}</button>
          <button className="danger" onClick={onRemove}><Trash2 size={15} /> Remove</button>
        </div>}
        <div className="library-meta">{item.title.rating !== null && <span><Star size={13} fill="currentColor" /> {item.title.rating.toFixed(1)}</span>}<span>{item.title.genres.slice(0, 2).join(" · ")}</span></div>
        <select value={item.status} onChange={(event) => onStatus(event.target.value as WatchStatus)} aria-label={`Status for ${item.title.name}`}>
          <option value="planned">Plan to watch</option><option value="watching">Watching</option><option value="watched">Watched</option><option value="dropped">Dropped</option>
        </select>
        {item.title.type === "tv" && item.title.episodesCount && item.title.episodesCount > 0 ? <div className="episode-stepper"><button onClick={() => { const next = Math.max(0, item.currentEpisode - 1); onProgress(next, next === 0 ? "planned" : "watching"); }} disabled={item.currentEpisode === 0} aria-label="Previous episode"><Minus size={13} /></button><span>Ep {item.currentEpisode} / {item.title.episodesCount}</span><button onClick={() => { const total = item.title.episodesCount ?? 0; const next = Math.min(total, item.currentEpisode + 1); onProgress(next, next === total ? "watched" : "watching"); }} disabled={item.currentEpisode === item.title.episodesCount} aria-label="Mark next episode watched"><Plus size={13} /></button><i style={{ width: `${Math.max(3, item.currentEpisode / item.title.episodesCount * 100)}%` }} /></div> : null}
      </div>
    </article>
  );
}

function ImportReportDialog({ report, onClose }: { report: MyDramaListImportReport; onClose(): void }) {
  return <div className="modal-layer" role="presentation"><article className="form-dialog import-report" role="dialog" aria-modal="true" aria-labelledby="import-report-title"><button className="icon-button modal-close" onClick={onClose} aria-label="Close import report"><X size={18} /></button><span className="report-check"><Check size={25} /></span><h1 id="import-report-title">Migration complete</h1><p className="dialog-copy">Matched titles now live in your account and will sync to every signed-in device.</p><div className="report-metrics"><span><strong>{report.imported}</strong>new</span><span><strong>{report.updated}</strong>updated</span><span><strong>{report.skipped}</strong>duplicates</span><span><strong>{report.unmatched.length}</strong>review</span></div>{report.unmatched.length > 0 && <div className="unmatched-list"><h2>Needs a manual match</h2>{report.unmatched.map((item) => <p key={`${item.title}-${item.year ?? ""}`}><strong>{item.title}</strong><span>{item.year ?? "Year unknown"} · {item.reason}</span></p>)}</div>}<button className="primary-button wide" onClick={onClose}>Done</button></article></div>;
}
